/**
 * Debug visibility levels for the contact-center timeline + structured-message
 * renderers. Hierarchical: `admin > agent > none`.
 *
 *  - `none`: Customer (chat widget, end-user view). Nothing internal-only.
 *  - `agent`: Any logged-in org member viewing CC V3. Tool-call cards,
 *    button `llmMessage` lines, basic timing info.
 *  - `admin`: Swivl staff (`member.swivlAdmin === true`). System prompts,
 *    LLM-call payloads, analytics events. Strict superset of `agent`.
 *
 * See docs/snowy/plans/2026-04-28-buttons-kind-plan.md §0.4.
 */
import { AccountKind } from "../Account/AccountKind";
import type { MemberInterface } from "../Members/Members.Interface";

export type DebugLevel = "none" | "agent" | "admin";

const ORDER: Record<DebugLevel, number> = { none: 0, agent: 1, admin: 2 };

/** Returns true if `current` is at-or-above `required`. */
export function debugLevelAtLeast(current: DebugLevel, required: DebugLevel): boolean {
  return ORDER[current] >= ORDER[required];
}

/**
 * The highest level the given member is allowed to see (the **ceiling**).
 * UI toggles can choose any level ≤ ceiling; server clamps requested level
 * to the ceiling on over-request. Pure function — same logic on both sides.
 */
export function getMaxDebugLevel(member: MemberInterface | null | undefined): DebugLevel {
  if (!member) return "none";
  // Staff of any kind may view the admin tier (Chuck, 2026-07-27). The kind
  // comes from the ACCOUNT — member.swivlAdmin is a non-persisted convenience
  // field that most load paths never stamp, which is how the Admin toggle
  // silently vanished (ENG-2946); keep honoring it as a fallback for callers
  // that stamped it but never rely on it alone.
  const kind = member.account?.kind;
  if (member.swivlAdmin || kind === AccountKind.SwivlAdmin || kind === AccountKind.SwivlStaff) return "admin";
  return "agent";
}

/**
 * The level a member's view should START at (Chuck, 2026-07-27): SwivlAdmin
 * accounts default to the admin tier; everyone else (staff included) defaults
 * to agent. A persisted user selection wins over this — see the CC store.
 */
export function getDefaultDebugLevel(member: MemberInterface | null | undefined): DebugLevel {
  if (!member) return "none";
  if (member.swivlAdmin || member.account?.kind === AccountKind.SwivlAdmin) return "admin";
  return "agent";
}

/** Server-side helper: clamp a requested level to the member's ceiling. */
export function clampDebugLevel(requested: DebugLevel, member: MemberInterface | null | undefined): DebugLevel {
  const ceiling = getMaxDebugLevel(member);
  return debugLevelAtLeast(requested, ceiling) ? ceiling : requested;
}
